// /team — cuentas del backoffice (M9.7).
//
// Acceso: nivel completo sobre Equipo (lib/access.ts). El perfil de cada
// cuenta se elige entre los de Equipo → Perfiles (admin_roles).
// Funciones: tabla + modal crear + modal editar + modal reset pwd + soft delete.
//
// Guards aplicados en backend tambien (defense in depth):
//   - admin no puede degradar/desactivar/eliminar a otro admin.
//   - admin no puede degradarse/desactivarse/eliminarse a si mismo.

import { FormEvent, useState } from 'react';
import { emailInputProps, normalizeEmail } from '@/lib/email-normalize';
import { Check } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAdmins,
  createAdmin,
  patchAdmin,
  deleteAdmin,
  resetAdminPassword,
  listRoles,
  adminMe,
  type AdminUserListItem,
  type AdminRole,
  type AdminRoleItem,
  type CreateAdminBody,
  type CreateAdminResult,
  AdminApiError,
} from '@/lib/api';
import { fieldCls, checkboxCls } from '@/lib/form-styles';
import TeamTabs from '@/components/TeamTabs';

type Modal =
  | { kind: 'create' }
  | { kind: 'edit'; target: AdminUserListItem }
  | { kind: 'reset'; target: AdminUserListItem }
  | { kind: 'delete'; target: AdminUserListItem }
  | { kind: 'pwd-shown'; email: string; password: string }
  | null;

// Espejo de la policy del pool admin de Cognito (ver libs/crypto/passwords.ts
// → assertValidAdminPassword). Mantener en sync. Devuelve el mensaje de error
// o null si la password es valida.
function validateAdminPassword(pwd: string): string | null {
  const missing: string[] = [];
  if (pwd.length < 14) missing.push('14 caracteres');
  if (!/[A-Z]/.test(pwd)) missing.push('una mayúscula');
  if (!/[a-z]/.test(pwd)) missing.push('una minúscula');
  if (!/[0-9]/.test(pwd)) missing.push('un número');
  if (!/[^A-Za-z0-9]/.test(pwd)) missing.push('un símbolo');
  return missing.length ? `La password debe tener al menos ${missing.join(', ')}.` : null;
}

function StatusBadge({ status }: { status: AdminUserListItem['status'] }): JSX.Element {
  const cls =
    status === 'active'
      ? 'bg-status-success/20 text-status-success'
      : status === 'suspended'
        ? 'bg-status-warning/20 text-status-warning'
        : 'bg-ink-muted/20 text-ink-muted';
  return (
    <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${cls}`}>{status}</span>
  );
}

function RoleBadge({ role, name }: { role: AdminRole; name: string }): JSX.Element {
  const cls = role === 'admin' ? 'bg-brand/20 text-brand' : 'bg-status-info/20 text-status-info';
  return (
    <span title={role} className={`text-xs rounded-full px-2 py-0.5 font-medium ${cls}`}>
      {name}
    </span>
  );
}

/** Selector de perfil. La lista sale de Equipo → Perfiles. */
function RoleSelect({
  value,
  onChange,
  disabled,
  roles,
}: {
  value: AdminRole;
  onChange: (role: AdminRole) => void;
  disabled?: boolean;
  roles: AdminRoleItem[] | undefined;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || !roles}
      className={fieldCls()}
    >
      {!roles ? <option value={value}>Cargando perfiles…</option> : null}
      {roles?.map((r) => (
        <option key={r.role_key} value={r.role_key}>
          {r.display_name} ({r.role_key})
        </option>
      ))}
    </select>
  );
}

export default function Admins(): JSX.Element {
  const qc = useQueryClient();
  const [modal, setModal] = useState<Modal>(null);

  const me = useQuery({ queryKey: ['admin-me'], queryFn: adminMe });
  const q = useQuery({ queryKey: ['admin-admins'], queryFn: listAdmins });
  const rolesQ = useQuery({ queryKey: ['admin-roles'], queryFn: listRoles });
  const myId = me.data?.admin_user_id;

  function refresh(): void {
    qc.invalidateQueries({ queryKey: ['admin-admins'] });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 data-help-page="team.page" data-help="team.page" className="text-2xl font-semibold">Equipo</h1>
          <p className="text-sm text-ink-muted">
            Las cuentas del propio backoffice. Cada una tiene un perfil de acceso.
          </p>
        </div>
        <button
          type="button"
          data-help="team.new"
          onClick={() => setModal({ kind: 'create' })}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4"
        >
          + Agregar
        </button>
      </header>

      <TeamTabs />

      {q.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {q.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {q.error instanceof AdminApiError ? q.error.message : 'No pudimos cargar los admins.'}
        </div>
      ) : null}

      {q.data ? (
        <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-help="team.permissions">
              <thead>
                <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                  <th className="text-left px-5 py-2">Email</th>
                  <th className="text-left px-5 py-2">Nombre</th>
                  <th className="text-left px-5 py-2">Perfil</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-left px-5 py-2">Last login</th>
                  <th className="text-right px-5 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((a) => {
                  const isSelf = myId === a.admin_user_id;
                  const isAdmin = a.role === 'admin';
                  const cantTouchRoleStatus = isSelf || isAdmin;

                  return (
                    <tr
                      key={a.admin_user_id}
                      className="border-b border-surface-border dark:border-surface-dark-border last:border-0"
                    >
                      <td className="px-5 py-2 break-all">{a.email}</td>
                      <td className="px-5 py-2">{a.display_name}</td>
                      <td className="px-5 py-2"><RoleBadge role={a.role} name={a.role_name} /></td>
                      <td className="px-5 py-2"><StatusBadge status={a.status} /></td>
                      <td className="px-5 py-2 tabular-nums text-ink-muted whitespace-nowrap">
                        {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-2 text-right whitespace-nowrap space-x-2">
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'edit', target: a })}
                          className="text-sm text-brand hover:underline"
                          title={cantTouchRoleStatus ? 'Solo display_name editable' : ''}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'reset', target: a })}
                          className="text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline"
                          disabled={a.status === 'deactivated'}
                        >
                          Reset pwd
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'delete', target: a })}
                          disabled={cantTouchRoleStatus || a.status === 'deactivated'}
                          className="text-sm text-status-error hover:underline disabled:opacity-30 disabled:no-underline disabled:cursor-not-allowed"
                          title={
                            isAdmin
                              ? 'No se puede eliminar a otro admin'
                              : isSelf
                                ? 'No se puede eliminar a si mismo'
                                : ''
                          }
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modal?.kind === 'create' ? (
        <CreateAdminModal
          roles={rolesQ.data}
          onClose={() => setModal(null)}
          onSuccess={(res) => {
            refresh();
            setModal({ kind: 'pwd-shown', email: res.email, password: res.password });
          }}
        />
      ) : null}
      {modal?.kind === 'edit' ? (
        <EditAdminModal
          target={modal.target}
          roles={rolesQ.data}
          isSelf={myId === modal.target.admin_user_id}
          onClose={() => setModal(null)}
          onSuccess={() => {
            refresh();
            setModal(null);
          }}
        />
      ) : null}
      {modal?.kind === 'reset' ? (
        <ResetPasswordModal
          target={modal.target}
          onClose={() => setModal(null)}
          onSuccess={(pwd) => {
            refresh();
            setModal({ kind: 'pwd-shown', email: modal.target.email, password: pwd });
          }}
        />
      ) : null}
      {modal?.kind === 'delete' ? (
        <DeleteAdminModal
          target={modal.target}
          onClose={() => setModal(null)}
          onSuccess={() => {
            refresh();
            setModal(null);
          }}
        />
      ) : null}
      {modal?.kind === 'pwd-shown' ? (
        <PasswordShownModal
          email={modal.email}
          password={modal.password}
          onClose={() => setModal(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

function ModalShell({ children, title, onClose }: { children: React.ReactNode; title: string; onClose: () => void }): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-xl shadow-lg w-full max-w-md mt-12 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink dark:hover:text-ink-inverted text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function CreateAdminModal({
  roles,
  onClose,
  onSuccess,
}: {
  roles: AdminRoleItem[] | undefined;
  onClose: () => void;
  onSuccess: (res: CreateAdminResult) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  // Arranca por el perfil mas bajo que exista: readonly si sigue, si no el
  // primero que no sea admin.
  const [role, setRole] = useState<AdminRole>(
    () => roles?.find((r) => r.role_key === 'readonly')?.role_key
      ?? roles?.find((r) => !r.is_builtin)?.role_key
      ?? 'readonly',
  );
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (body: CreateAdminBody) => createAdmin(body),
    onSuccess,
    onError: (e) =>
      setErr(e instanceof AdminApiError ? e.message : 'No pudimos crear el admin.'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setErr(null);
    const pwd = password.trim();
    // Validar contra la policy del pool admin ANTES de enviar. Si la dejamos
    // llegar y Cognito la rechaza, el backend lo maneja, pero asi damos feedback
    // inmediato. (Vacio = el backend autogenera una password valida.)
    if (pwd) {
      const problem = validateAdminPassword(pwd);
      if (problem) {
        setErr(problem);
        return;
      }
    }
    const body: CreateAdminBody = { email: normalizeEmail(email), display_name: displayName, role };
    if (pwd) body.password = pwd;
    m.mutate(body);
  }

  return (
    <ModalShell title="Crear admin" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            {...emailInputProps(setEmail)}
            className={fieldCls()}
          />
        </Field>
        <Field label="Nombre">
          <input
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={fieldCls()}
          />
        </Field>
        <Field label="Perfil de acceso">
          <RoleSelect value={role} onChange={setRole} roles={roles} />
        </Field>
        <Field label="Password (opcional — se autogenera si vacío)">
          <input
            type="text"
            placeholder="dejar vacío para autogenerar"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={fieldCls()}
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-gray-500">
            Mínimo 14 caracteres, con mayúscula, minúscula, número y símbolo.
          </p>
        </Field>
        {err ? <ErrorBox>{err}</ErrorBox> : null}
        <Actions>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancelar</button>
          <button type="submit" disabled={m.isPending} className={btnPrimary}>
            {m.isPending ? 'Creando…' : 'Crear'}
          </button>
        </Actions>
      </form>
    </ModalShell>
  );
}

function EditAdminModal({
  target,
  roles,
  isSelf,
  onClose,
  onSuccess,
}: {
  target: AdminUserListItem;
  roles: AdminRoleItem[] | undefined;
  isSelf: boolean;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const [displayName, setDisplayName] = useState(target.display_name);
  const [role, setRole] = useState<AdminRole>(target.role);
  const [status, setStatus] = useState<'active' | 'suspended'>(
    target.status === 'deactivated' ? 'active' : (target.status as 'active' | 'suspended'),
  );
  const [dangerZone, setDangerZone] = useState(target.permissions?.['danger_zone'] === true);
  const [err, setErr] = useState<string | null>(null);

  // Guards: si es self o el target es admin, role/status no se tocan.
  const lockRoleStatus = isSelf || target.role === 'admin';

  const m = useMutation({
    mutationFn: () => {
      const patch: Parameters<typeof patchAdmin>[1] = {};
      if (displayName !== target.display_name) patch.display_name = displayName;
      if (!lockRoleStatus && role !== target.role) patch.role = role;
      if (!lockRoleStatus && status !== target.status) patch.status = status;
      // Permisos: editables incluso en admins (a diferencia de role/status) —
      // si no, seria imposible otorgar danger_zone a un par. El backend revoca
      // las sesiones del target al cambiarlos.
      if (dangerZone !== (target.permissions?.['danger_zone'] === true)) {
        patch.permissions = { ...target.permissions, danger_zone: dangerZone };
      }
      return patchAdmin(target.admin_user_id, patch);
    },
    onSuccess,
    onError: (e) =>
      setErr(e instanceof AdminApiError ? e.message : 'No pudimos guardar los cambios.'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setErr(null);
    m.mutate();
  }

  return (
    <ModalShell title={`Editar — ${target.email}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Nombre">
          <input
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={fieldCls()}
          />
        </Field>
        <Field label="Perfil de acceso">
          <RoleSelect value={role} onChange={setRole} disabled={lockRoleStatus} roles={roles} />
          {lockRoleStatus ? (
            <p className="text-xs text-ink-muted mt-1">
              {isSelf
                ? 'No podés modificar tu propio perfil.'
                : 'No se puede modificar el perfil de otra cuenta admin.'}
            </p>
          ) : null}
        </Field>
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'suspended')}
            disabled={lockRoleStatus}
            className={fieldCls()}
          >
            <option value="active">active</option>
            <option value="suspended">suspended</option>
          </select>
        </Field>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={dangerZone}
            onChange={(e) => setDangerZone(e.target.checked)}
            className={checkboxCls('mt-0.5')}
          />
          <span>
            <span className="font-medium">Danger Zone</span>
            <span className="block text-xs text-ink-muted">
              Habilita la pestaña Danger Zone del partner (switches sensibles y medios de pago).
              Cambiarlo revoca las sesiones del admin — va a tener que volver a loguearse.
            </span>
          </span>
        </label>
        {err ? <ErrorBox>{err}</ErrorBox> : null}
        <Actions>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancelar</button>
          <button type="submit" disabled={m.isPending} className={btnPrimary}>
            {m.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </Actions>
      </form>
    </ModalShell>
  );
}

function ResetPasswordModal({
  target,
  onClose,
  onSuccess,
}: {
  target: AdminUserListItem;
  onClose: () => void;
  onSuccess: (pwd: string) => void;
}): JSX.Element {
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => resetAdminPassword(target.admin_user_id),
    onSuccess: (res) => onSuccess(res.password),
    onError: (e) =>
      setErr(e instanceof AdminApiError ? e.message : 'No pudimos resetear la pwd.'),
  });

  return (
    <ModalShell title={`Resetear pwd — ${target.email}`} onClose={onClose}>
      <p className="text-sm text-ink-muted">
        Se generará una password aleatoria nueva. Todas las sesiones activas del admin
        serán revocadas. La pwd se mostrará una sola vez.
      </p>
      {err ? <ErrorBox>{err}</ErrorBox> : null}
      <Actions>
        <button type="button" onClick={onClose} className={btnSecondary}>Cancelar</button>
        <button
          type="button"
          onClick={() => { setErr(null); m.mutate(); }}
          disabled={m.isPending}
          className={btnPrimary}
        >
          {m.isPending ? 'Reseteando…' : 'Resetear'}
        </button>
      </Actions>
    </ModalShell>
  );
}

function DeleteAdminModal({
  target,
  onClose,
  onSuccess,
}: {
  target: AdminUserListItem;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => deleteAdmin(target.admin_user_id),
    onSuccess,
    onError: (e) =>
      setErr(e instanceof AdminApiError ? e.message : 'No pudimos eliminar.'),
  });

  return (
    <ModalShell title={`Eliminar — ${target.email}`} onClose={onClose}>
      <p className="text-sm">
        Soft-delete: el admin queda con <code className="font-mono">status=deactivated</code>,
        Cognito disable, y todas las sesiones revocadas. Reversible via Editar → Status: active.
      </p>
      {err ? <ErrorBox>{err}</ErrorBox> : null}
      <Actions>
        <button type="button" onClick={onClose} className={btnSecondary}>Cancelar</button>
        <button
          type="button"
          onClick={() => { setErr(null); m.mutate(); }}
          disabled={m.isPending}
          className="min-h-[2.75rem] rounded-lg bg-status-error hover:opacity-90 text-white font-medium px-4 disabled:opacity-50"
        >
          {m.isPending ? 'Eliminando…' : 'Eliminar'}
        </button>
      </Actions>
    </ModalShell>
  );
}

function PasswordShownModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  function copy(): void {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => { /* ignore */ });
  }

  return (
    <ModalShell title="Password generada" onClose={onClose}>
      <p className="text-sm">
        Para <strong>{email}</strong>. <em>Esta pwd se muestra una sola vez</em> — copiala y compartila por canal seguro.
      </p>
      <div className="bg-surface-muted dark:bg-surface-dark-muted rounded-lg p-3 font-mono text-sm break-all">
        {password}
      </div>
      <Actions>
        <button type="button" onClick={copy} className={btnSecondary}>
          {copied ? <span className="inline-flex items-center gap-1"><Check size={12} aria-hidden="true" /> Copiada</span> : 'Copiar'}
        </button>
        <button type="button" onClick={onClose} className={btnPrimary}>Cerrar</button>
      </Actions>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

const btnPrimary =
  'min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4 disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary =
  'min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
      {children}
    </div>
  );
}

function Actions({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="flex justify-end gap-2 pt-2">{children}</div>;
}
